import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDataTable from 'components/KNDataTable/KNDataTable'
import type { KNDataTableAction, KNDataTableColumn } from 'components/KNDataTable/types'
import KNConfirmationDialog from 'components/KNDialog/KNConfirmationDialog'
import { KNDirectionArrow, KNMultiDirectionArrow } from 'components/KNIcon/KNIcon'
import { analyticsEvent } from 'global/helpers/analytics'
import useDialog from 'global/hooks/useDialog'
import { Fragment, type ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { deleteLane, deleteRegion, LANES_QUERY_KEY, REGIONS_QUERY_KEY } from './CarrierProfile.service'
import { type Contact, isLane, isRegion, type Lane, LaneDirection, type Region } from './CarrierProfile.types'
import CreateOrEditLaneDialog from './CreateOrEditLaneDialog'
import CreateOrEditRegionDialog from './CreateOrEditRegionDialog'

const getDirectionIcon = (direction: LaneDirection): ReactElement => {
  switch (direction) {
    case LaneDirection.ONE_WAY:
      return <KNDirectionArrow fontSize="small" />
    case LaneDirection.ROUND_TRIP:
      return <KNMultiDirectionArrow fontSize="small" />
  }
}

interface LaneAndRegionTableProps {
  lanesAndRegions: (Lane | Region)[]
  contacts: Contact[]
}

const LanesAndRegionsTable = ({
  lanesAndRegions,
  contacts,
}: LaneAndRegionTableProps): ReactElement => {
  const { t } = useTranslation()
  const [activeLaneOrRegion, setActiveLaneOrRegion] = useState<Lane | Region>()
  const queryClient = useQueryClient()
  const deleteLaneMutation = useMutation({
    mutationFn: deleteLane,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LANES_QUERY_KEY] }),
  })
  const deleteRegionMutation = useMutation({
    mutationFn: deleteRegion,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [REGIONS_QUERY_KEY] }),
  })
  const editLaneDialog = useDialog<typeof CreateOrEditLaneDialog>()
  const editRegionDialog = useDialog<typeof CreateOrEditRegionDialog>()

  const handleEditClick = useCallback((row: Lane | Region) => {
    setActiveLaneOrRegion(row)
    if (isLane(row)) {
      editLaneDialog.handleOpen()
    } else if (isRegion(row)) {
      editRegionDialog.handleOpen()
    }
  }, [])

  const handleDeleteLaneDialogAction = useCallback(async () => {
    if (!activeLaneOrRegion || !isLane(activeLaneOrRegion)) {
      return
    }
    await deleteLaneMutation.mutateAsync(activeLaneOrRegion.id)
    analyticsEvent('polestar_carrier_profile_lane_removed')
  }, [activeLaneOrRegion])

  const deleteLaneDialog = useDialog<typeof KNConfirmationDialog>({
    onAction: handleDeleteLaneDialogAction,
  })

  const handleDeleteRegionDialogAction = useCallback(async () => {
    if (!activeLaneOrRegion || !isRegion(activeLaneOrRegion)) {
      return
    }
    await deleteRegionMutation.mutateAsync(activeLaneOrRegion.id)
    analyticsEvent('polestar_carrier_profile_region_removed')
  }, [activeLaneOrRegion])

  const deleteRegionDialog = useDialog<typeof KNConfirmationDialog>({
    onAction: handleDeleteRegionDialogAction,
  })

  const handleDeleteClick = useCallback((row: Region | Lane) => {
    setActiveLaneOrRegion(row)
    if (isLane(row)) {
      deleteLaneDialog.handleOpen()
    } else if (isRegion(row)) {
      deleteRegionDialog.handleOpen()
    }
  }, [])

  const actions = useMemo((): KNDataTableAction<Lane | Region>[] => [
    {
      name: 'edit',
      label: t('screens.cs.carrier_profile.lanes_and_regions.edit_lane'),
      icon: <EditIcon />,
      onClick: handleEditClick,
    },
    {
      name: 'delete',
      label: t('general.delete'),
      icon: <DeleteIcon />,
      onClick: handleDeleteClick,
    },
  ], [])

  const typeColumnValue = (row: Lane | Region): ReactElement => (
    <Typography variant="p3" color="primary.main">
      {isLane(row) && t('screens.cs.carrier_profile.lanes_and_regions.LANE')}
      {isRegion(row) && t('screens.cs.carrier_profile.lanes_and_regions.REGION')}
    </Typography>
  )

  const locationColumnValue = (row: Lane | Region): ReactElement => (
    <Stack spacing={1} direction="row">
      {isLane(row) && (
        <>
          <Typography variant="p3" color="primary.main">
            {row.startLocation.name}
          </Typography>
          {getDirectionIcon(row.direction)}
          <Typography variant="p3" color="primary.main">
            {row.endLocation.name}
          </Typography>
        </>
      )}
      {isRegion(row) && (
        <Typography variant="p3" color="primary.main">
          {row.location.name}
        </Typography>
      )}
    </Stack>
  )

  const vehiclesColumnValue = (row: Lane | Region): ReactElement => (
    <Typography variant="p3" color="primary.main">
      {row.vehiclesAmount}
    </Typography>
  )

  const contactsColumnValue = (row: Lane | Region): ReactElement => (
    <Stack spacing={0.5}>
      {row.contacts?.map((contact) => (
        <Fragment key={contact.id}>
          <Typography variant="p3" color="primary.main" noWrap>
            {contact.name}
          </Typography>
          <Typography variant="p5" color="primary.light" noWrap>
            {contact.phone}
          </Typography>
          <Typography variant="p5" color="primary.light" noWrap>
            {contact.email}
          </Typography>
        </Fragment>
      ))}
    </Stack>
  )

  const columns = useMemo((): KNDataTableColumn<Lane | Region>[] => [
    {
      name: 'type',
      label: t('screens.cs.carrier_profile.lanes_and_regions.type'),
      getValue: typeColumnValue,
      sx: { width: { xs: '10rem', xxl: '14rem' } },
    },
    {
      name: 'location',
      label: t('screens.cs.carrier_profile.lanes_and_regions.location'),
      getValue: locationColumnValue,
      sx: { width: { xs: '10rem', xxl: 'auto' } },
    },
    {
      name: 'vehicles',
      label: t('screens.cs.carrier_profile.lanes_and_regions.vehicles'),
      getValue: vehiclesColumnValue,
      sx: { width: '10rem' },
    },
    {
      name: 'contacts',
      label: t('screens.cs.carrier_profile.lanes_and_regions.contacts'),
      getValue: contactsColumnValue,
      sx: { width: { xs: '10rem', xxl: '20rem' } },
    },
  ], [])

  return (
    <>
      <KNDataTable
        columns={columns}
        actions={actions}
        data={lanesAndRegions ?? []}
        sx={{
          marginX: -2,
        }}
      />
      {activeLaneOrRegion && (
        <>
          <CreateOrEditLaneDialog
            payload={{
              contacts,
              lane: activeLaneOrRegion as Lane,
            }}
            open={editLaneDialog.isOpen}
            onAction={editLaneDialog.handleAction}
            onClose={editLaneDialog.handleClose}
          />
          <KNConfirmationDialog
            message={t('screens.cs.carrier_profile.lanes_and_regions.lane_delete_confirmation')}
            continueLabel={t('general.delete')}
            open={deleteLaneDialog.isOpen}
            onAction={deleteLaneDialog.handleAction}
            onClose={deleteLaneDialog.handleClose}
          />
          <CreateOrEditRegionDialog
            payload={{
              contacts,
              region: activeLaneOrRegion as Region,
            }}
            open={editRegionDialog.isOpen}
            onAction={editRegionDialog.handleAction}
            onClose={editRegionDialog.handleClose}
          />
          <KNConfirmationDialog
            message={t('screens.cs.carrier_profile.lanes_and_regions.region_delete_confirmation')}
            continueLabel={t('general.delete')}
            open={deleteRegionDialog.isOpen}
            onAction={deleteRegionDialog.handleAction}
            onClose={deleteRegionDialog.handleClose}
          />
        </>
      )}
    </>
  )
}

export default LanesAndRegionsTable
